// =============================================================
//  PriceBoxes — Caixa(s) de preço do vídeo (Remotion)
// =============================================================
//  Renderiza 1 ou 2 caixas de preço dependendo de produto.tipopreco:
//    simples     -> 1 caixa  (R$ X)
//    dexpory     -> 2 caixas (DE Y riscado, POR X em destaque)
//    pagamento   -> 2 caixas (CARTÃO Y, PIX X)  ← rótulos do front
//    levexpaguey -> 1 caixa  (LEVE 3 PAGUE 19,90)
//    parcelado   -> 1 caixa
//    customizado -> 1 caixa
//
// ─── ONDE AJUSTAR ───────────────────────────────────────────────
//  • Cores / fontes / paddings / arredondamento: constantes STYLE
//    abaixo (logo após os imports). Tudo centralizado.
//  • Tamanho geral: prop `fontSize` (vinda do layout do ProductVideo,
//    seção getLayoutByFormato → preco.fontSize).
//  • Proporção DE vs POR: `STYLE.proporcaoDe` (0.75 = 75% do POR).
//  • Inclinação do "DE": `STYLE.rotacaoDe`.
//  • Espessura do X de corte: `STYLE.corteStroke`.
//  • Cor do X: `STYLE.corteCor`.
// ────────────────────────────────────────────────────────────────

import React from 'react';

// =============================================================
//  STYLE — mexa AQUI para microajustes visuais
// =============================================================
const STYLE = {
  // Proporção do card "DE" em relação ao "POR" (1 = mesmo tamanho)
  proporcaoDe: 0.62,
  // Rotação do card DE
  rotacaoDe: '-4deg',
  // Cores do card "DE" (preço antigo)
  deBg: '#FFD400',
  deCor: '#2b1a05',
  // Cor e espessura do X de cancelamento
  corteCor: '#c0150f',
  corteStroke: 10,
  // Bordas, sombras, paddings
  radius: 26,
  paddingMaior: '40px 56px',
  paddingMenor: '28px 36px',
  shadow: '0 12px 35px rgba(0,0,0,0.35)',
  // Espaço entre os cards DE e POR
  gap: 30,
  // Pesos tipográficos
  pesoLabel: 800,
  pesoValor: 900,
  // Badge "DE" / "POR" (rótulo absoluto no topo do card)
  badgeBg: '#1c1c1c',
  badgeCor: '#fff',
  badgeRadius: 999,
  badgePadding: '8px 22px',
  badgeOffsetY: -22,
  badgeRotacao: '-4deg',
};

const baseBox = {
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  borderRadius: STYLE.radius,
  fontWeight: STYLE.pesoValor,
  textAlign: 'center',
  boxShadow: STYLE.shadow,
  lineHeight: 1,
};

function Box({ children, color, bg, style }) {
  return (
    <div style={{ ...baseBox, color, background: bg, ...style }}>
      {children}
    </div>
  );
}

// "R$ 16,00" — número com centavos menores (estilo etiqueta de oferta)
function Valor({ valor, fontSize, riscado = false }) {
  const raw = String(valor || '0,00');
  const partes = (raw.includes(',') ? raw : raw.replace('.', ',')).split(',');
  const reais = partes[0] || '0';
  const centavos = (partes[1] || '00').padEnd(2, '0').slice(0, 2);
  return (
    <div style={{ display: 'inline-flex', alignItems: 'baseline', justifyContent: 'center', gap: 4, position: 'relative' }}>
      <span style={{
        fontSize: fontSize * 0.36,
        fontWeight: 700,
        alignSelf: 'flex-start',
        marginTop: fontSize * 0.12,
      }}>
        R$
      </span>
      <span style={{
        fontSize,
        letterSpacing: '-2px',
        fontWeight: STYLE.pesoValor,
        textDecoration: riscado ? 'line-through' : 'none',
      }}>
        {reais}
      </span>
      <span style={{
        fontSize: fontSize * 0.55,
        fontWeight: STYLE.pesoValor,
        textDecoration: riscado ? 'line-through' : 'none',
      }}>
        ,{centavos}
      </span>
    </div>
  );
}

function Label({ children, fontSize, style }) {
  return (
    <span style={{
      fontSize: fontSize * 0.34,
      fontWeight: STYLE.pesoLabel,
      letterSpacing: 1,
      textTransform: 'uppercase',
      opacity: 0.95,
      marginBottom: 4,
      ...style,
    }}>
      {children}
    </span>
  );
}

// Rótulo absoluto (badge) que fica colado por cima do card — estilo "DE" / "POR"
function Badge({ children, fontSize, style }) {
  return (
    <span style={{
      position: 'absolute',
      top: STYLE.badgeOffsetY,
      left: '50%',
      transform: `translateX(-50%) rotate(${STYLE.badgeRotacao})`,
      background: STYLE.badgeBg,
      color: STYLE.badgeCor,
      padding: STYLE.badgePadding,
      borderRadius: STYLE.badgeRadius,
      fontSize: fontSize * 0.32,
      fontWeight: STYLE.pesoValor,
      letterSpacing: 1,
      textTransform: 'uppercase',
      lineHeight: 1,
      whiteSpace: 'nowrap',
      boxShadow: '0 4px 12px rgba(0,0,0,0.35)',
      zIndex: 2,
      ...style,
    }}>
      {children}
    </span>
  );
}

// X riscando o valor do "DE"
function CruzCorte({ color = STYLE.corteCor, strokeWidth = STYLE.corteStroke }) {
  return (
    <svg
      viewBox="0 0 100 100"
      preserveAspectRatio="none"
      style={{ position: 'absolute', inset: -10, width: 'calc(100% + 20px)', height: 'calc(100% + 20px)', pointerEvents: 'none' }}
    >
      <line x1="4" y1="4" x2="96" y2="96" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
      <line x1="96" y1="4" x2="4" y2="96" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
    </svg>
  );
}

export default function PriceBoxes({
  produto,
  fonte,
  cor = '#fff',
  bg = '#73207C',
  fontSize = 110,
  gap = STYLE.gap,
}) {
  if (!produto) return null;
  const fz = fontSize;
  const boxStyle = { fontFamily: fonte };
  const tipo = produto.tipopreco;

  // ──────────────────────── DE / POR ────────────────────────
  if (tipo === 'dexpory' && produto.precode && produto.precode !== '0,00') {
    const fzPor = fz;
    const fzDe  = fz * STYLE.proporcaoDe;
    return (
      <div style={{ display: 'flex', gap, alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'center', whiteSpace: 'nowrap', paddingTop: 24 }}>
        {/* Card DE (menor, riscado) — usa as mesmas cores do tema do POR */}
        <Box color={cor} bg={bg} style={{
          ...boxStyle,
          transform: `rotate(${STYLE.rotacaoDe})`,
          padding: STYLE.paddingMenor,
          flex: '0 0 auto',
        }}>
          <Badge fontSize={fzDe}>De</Badge>
          <div style={{ position: 'relative', padding: '0 10px' }}>
            <Valor valor={produto.precode} fontSize={fzDe} />
            <CruzCorte />
          </div>
        </Box>

        {/* Card POR (maior, destaque) */}
        <Box color={cor} bg={bg} style={{ ...boxStyle, padding: STYLE.paddingMaior, flex: '0 0 auto' }}>
          <Badge fontSize={fzPor}>Por</Badge>
          <Valor valor={produto.precopor} fontSize={fzPor} />
        </Box>
      </div>
    );
  }

  // ──────────────────────── PAGAMENTO ─────────────────────────
  if (tipo === 'pagamento') {
    const l1 = produto.labelPrecoPrincipal || produto.titulopreconormal || 'CARTÃO';
    const l2 = produto.labelPrecoSecundario || produto.tituloprecoparcelado || 'PIX';
    const v1 = produto.precoPrincipal || produto.precode || produto.preco;
    const v2 = produto.precoSecundario || produto.precopor || produto.preco;
    const fz1 = fz * 0.82;
    return (
      <div style={{ display: 'flex', gap, alignItems: 'center', flexWrap: 'nowrap', justifyContent: 'center', whiteSpace: 'nowrap', paddingTop: 24 }}>
        <Box color={cor} bg={bg} style={{ ...boxStyle, padding: STYLE.paddingMenor, flex: '0 0 auto' }}>
          <Badge fontSize={fz1}>{l1}</Badge>
          <Valor valor={v1} fontSize={fz1} />
        </Box>
        <Box color={cor} bg={bg} style={{ ...boxStyle, padding: STYLE.paddingMaior, flex: '0 0 auto' }}>
          <Badge fontSize={fz}>{l2}</Badge>
          <Valor valor={v2} fontSize={fz} />
        </Box>
      </div>
    );
  }

  // ──────────────────────── LEVE x PAGUE y ─────────────────
  if (tipo === 'levexpaguey') {
    return (
      <Box color={cor} bg={bg} style={{ ...boxStyle, padding: STYLE.paddingMaior }}>
        {produto.titulodesconto && <Label fontSize={fz}>{produto.titulodesconto}</Label>}
        <Valor valor={produto.precodesconto} fontSize={fz} />
      </Box>
    );
  }

  // ──────────────────────── PARCELADO ──────────────────────
  if (tipo === 'parcelado') {
    return (
      <Box color={cor} bg={bg} style={{ ...boxStyle, padding: STYLE.paddingMaior }}>
        {produto.tituloprecoparcelado && <Label fontSize={fz}>{produto.tituloprecoparcelado}</Label>}
        <Valor valor={produto.precoparcelado} fontSize={fz} />
        {produto.precoavista && produto.precoavista !== '0,00' && (
          <div style={{ marginTop: 10, fontSize: fz * 0.28, fontWeight: 700, opacity: 0.85 }}>
            {produto.titulopreconormal || 'À VISTA'} R$ {produto.precoavista}
          </div>
        )}
      </Box>
    );
  }

  // ──────────────────────── CUSTOMIZADO ────────────────────
  if (tipo === 'customizado') {
    return (
      <Box color={cor} bg={bg} style={{ ...boxStyle, fontSize: fz, padding: STYLE.paddingMaior }}>
        {produto.precocustomizado}
      </Box>
    );
  }

  // ──────────────────────── SIMPLES (default) ──────────────
  return (
    <Box color={cor} bg={bg} style={{ ...boxStyle, padding: STYLE.paddingMaior }}>
      <Valor valor={produto.preco} fontSize={fz} />
    </Box>
  );
}
